import { useEffect, useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';

import { Icon } from './icon';
import { Button } from './shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn/select';
import { ScrollableTable } from './table/scrollable-table';
import { UserV2 } from '../../../../backend/src/user/user.entity';
import { useUpdateUser } from '@/api/mutations/useUpdateUser';
import useFetchOrgUsers from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useGetAllOrganisationUserRoles } from '@/api/queries/useGetOrganisationUserRoles';
import { useLazyUserMetadata } from '@/api/queries/useGetUserMetadata';
import { useUserContext } from '@/Contexts/User';

const fallbackTableData: UserV2[] = [];
const columnHelper = createColumnHelper<UserV2>();

// Helper component to render last active date with lazy loading
function LastActiveCell({ userId, metadata }: { userId: string; metadata: ReturnType<typeof useLazyUserMetadata> }) {
  const { getMetadata, hasMetadata, isPending } = metadata;
  const userMetadata = getMetadata(userId);

  if (isPending(userId) || !hasMetadata(userId)) {
    return <div className="h-4 w-full animate-pulse rounded-md bg-gray-200/80" />;
  }

  if (!userMetadata) {
    return <span className="text-gray-500">Never</span>;
  }

  const date = new Date(userMetadata.lastActiveAt * 1000);
  return <span>{moment(date).fromNow()}</span>;
}

export function UsersTable({
  organisationId,
  onViewUserMatters,
}: {
  organisationId: string;
  onViewUserMatters?: (user: UserV2) => void;
}) {
  const { user } = useUserContext();
  if (!user) return null;

  const { data: users } = useFetchOrgUsers([organisationId]);
  const { data: roles } = useGetAllOrganisationUserRoles(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');

  // Use lazy user metadata hook
  const userMetadata = useLazyUserMetadata();
  const [, setMetadataRefresh] = useState(0);

  const { mutate: updateUser } = useUpdateUser();

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        size: 200,
        header: () => <span>Email Address</span>,
        cell: (info) => {
          const disabled = info.row.original.disabled;
          return (
            <div style={{ width: info.column.getSize() }} className={`${disabled ? 'text-gray-400' : ''}`}>
              {info.getValue()}
              {disabled && (
                <span className="mx-2 rounded border border-gray-300 bg-gray-100 px-1 py-0.5 text-xs text-gray-500">
                  Disabled
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('role', {
        size: 100,
        header: () => <span>Role</span>,
        cell: (info) => {
          const role = info.row.original.role;
          return (!user.organisation.isGlobalAdmin && role.name === 'Administrator') || info.row.original.disabled ? (
            <div className="px-0.5 py-0.5 pl-1.5 text-gray-500">{role.name}</div>
          ) : (
            <Select value={role.id} onValueChange={(roleId) => updateUser({ userId: info.row.original.id, roleId })}>
              <SelectTrigger className="-m-0.5 h-auto w-36 border-none p-1 pl-2 focus:outline-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(roles ?? [])
                  .filter((r) => r.name !== 'Administrator' || user?.organisation.isGlobalAdmin === true)
                  .map((roleItem) => (
                    <SelectItem key={roleItem.id} value={roleItem.id}>
                      {roleItem.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          );
        },
      }),
      columnHelper.accessor('hasBeenActive', {
        header: () => <span>Onboarded</span>,
        cell: (info) =>
          info.getValue() ? (
            <span className="flex items-center gap-2">
              Yes <Icon className="lucide--check-circle-2" />
            </span>
          ) : (
            <span className="text-gray-500">No</span>
          ),
      }),
      columnHelper.accessor('matters', {
        size: 10,
        header: () => <span>No. of Matters</span>,
        cell: (info) => <div style={{ width: info.column.getSize() }}>{info.getValue().length}</div>,
      }),
      ...(onViewUserMatters
        ? [
            columnHelper.display({
              id: 'manageMatters',
              cell: (info) => (
                <Button size="sm" onClick={() => onViewUserMatters(info.row.original)}>
                  Manage Matters
                </Button>
              ),
            }),
          ]
        : []),
      columnHelper.display({
        id: 'lastActiveAt',
        header: () => <span>Last Active</span>,
        cell: (info) => (
          <div className="w-32">
            <LastActiveCell userId={info.row.original.id} metadata={userMetadata} />
          </div>
        ),
      }),
    ],
    [userMetadata, roles, user, onViewUserMatters],
  );

  const table = useReactTable({
    data: users ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  // Fetch metadata for currently visible users
  useEffect(() => {
    const visibleRows = table.getRowModel().rows;
    const visibleUserIds = visibleRows.map((row) => row.original.id);

    if (visibleUserIds.length > 0) {
      userMetadata.fetchMetadata(visibleUserIds).then(() => {
        // Force a re-render to show the fetched metadata
        setMetadataRefresh((prev) => prev + 1);
      });
    }
  }, [table.getState().pagination.pageIndex, users, globalFilter, userMetadata.cacheVersion]);

  return <ScrollableTable table={table} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} plural="users" />;
}
